<script setup lang="ts">
import { gsap } from 'gsap'

const images = ['/mogbille_default.png', '/mogbille_fly1.png', '/mogbille_fly2.png']
const currentImage = ref(images[0]) // Default image is mogbille_default
const { width: innerWidth, height: innerHeight } = useWindowSize()
let tickerAdded = false
let lastMouseX = innerWidth.value / 2
let lastMouseY = innerHeight.value / 2
let lastMoveTime = performance.now()
let animationFrame = 0
let animationSpeed = 0

onMounted(() => {
  const cursor = document.querySelector('.mogbille-cursor') as HTMLElement
  if (!cursor) return

  gsap.set(cursor, { xPercent: -50, yPercent: -50 })

  const pos = { x: innerWidth.value / 2, y: innerHeight.value / 2 }
  const mouse = { x: pos.x, y: pos.y }
  const speed = 0.05

  const xSet = gsap.quickSetter(cursor, 'x', 'px')
  const ySet = gsap.quickSetter(cursor, 'y', 'px')

  let timer: ReturnType<typeof setTimeout> | null = null
  let autonomous = false

  const resetTimer = () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      autonomous = true
      startBugCrawl()
      currentImage.value = images[0] // Reset to default when idle
    }, 3000)
  }

  const startBugCrawl = () => {
    if (!autonomous) return

    const bugMove = () => {
      if (!autonomous) return

      const randomAngle = Math.random() * Math.PI * 2
      const randomDistance = Math.random() * 30 + 10
      const newX = Math.max(
        0,
        Math.min(innerWidth.value, mouse.x + Math.cos(randomAngle) * randomDistance),
      )
      const newY = Math.max(
        0,
        Math.min(innerHeight.value, mouse.y + Math.sin(randomAngle) * randomDistance),
      )

      gsap.to(mouse, {
        duration: Math.random() * 0.5 + 0.1,
        x: newX,
        y: newY,
        ease: 'none',
        onUpdate: () => {
          updateRotation(newX, newY)
        },
        onComplete: () => {
          if (autonomous) {
            setTimeout(bugMove, Math.random() * 400 + 100)
          }
        },
      })
    }

    bugMove()
  }

  const updateRotation = (newX: number, newY: number) => {
    const dx = newX - pos.x
    const dy = newY - pos.y
    const targetRotation = Math.atan2(dy, dx) * (180 / Math.PI)

    gsap.to(cursor, {
      rotation: targetRotation,
      duration: 0.3,
      ease: 'power2.out',
    })
  }

  const handleMouseMove = (e: MouseEvent) => {
    const now = performance.now()
    const timeDiff = now - lastMoveTime

    // Calculate acceleration (distance moved per time unit)
    const dx = e.x - lastMouseX
    const dy = e.y - lastMouseY
    const distance = Math.sqrt(dx * dx + dy * dy)
    animationSpeed = Math.min(5, distance / timeDiff) // Capped speed

    lastMouseX = e.x
    lastMouseY = e.y
    lastMoveTime = now

    mouse.x = e.x
    mouse.y = e.y
    if (autonomous) {
      autonomous = false
      gsap.killTweensOf(mouse)
    }

    // Switch images based on mouse movement
    if (distance > 10) {
      const imageIndex = Math.floor(Math.random() * 2) + 1 // Either 1 or 2 for fly images
      currentImage.value = images[imageIndex]
    }

    resetTimer()
  }

  window.addEventListener('mousemove', handleMouseMove)
  resetTimer()

  const tickerCallback = () => {
    const dt = 1.0 - Math.pow(1.0 - speed, gsap.ticker.deltaRatio())
    pos.x += (mouse.x - pos.x) * dt
    pos.y += (mouse.y - pos.y) * dt
    xSet(pos.x)
    ySet(pos.y)

    if (!autonomous) {
      const dx = mouse.x - pos.x
      const dy = mouse.y - pos.y
      const rotation = Math.atan2(dy, dx)
      gsap.set(cursor, { rotation: rotation + '_rad' })
    }

    // Calculate distance from cursor to mouse position
    const distanceToMouse = Math.sqrt(Math.pow(mouse.x - pos.x, 2) + Math.pow(mouse.y - pos.y, 2))

    // Adjust animation speed based on the distance to mouse
    const maxSpeed = 20
    const minSpeed = 2
    const maxDistance = Math.max(innerWidth.value, innerHeight.value)
    animationSpeed = Math.min(maxSpeed, minSpeed + (distanceToMouse / maxDistance) * maxSpeed)

    // Handle animation frames based on speed
    if (animationSpeed > 0) {
      animationFrame = (animationFrame + animationSpeed * 0.2) % 3 // Loops between 0, 1, and 2
      if (!autonomous) {
        // Only switch images when not autonomous
        currentImage.value = images[Math.floor(animationFrame)] // Cycles through the fly images
      }
    } else {
      currentImage.value = images[0] // Reset to default
    }
  }

  if (!tickerAdded) {
    gsap.ticker.add(tickerCallback)
    tickerAdded = true
  }

  onUnmounted(() => {
    window.removeEventListener('mousemove', handleMouseMove)
    if (timer) clearTimeout(timer)
    gsap.ticker.remove(tickerCallback)
    gsap.killTweensOf(mouse)
  })
})
</script>

<template>
  <div
    class="mogbille-cursor pointer-events-none fixed top-0 left-0 z-100 h-[15vw] w-[15vw] -translate-x-1/2 -translate-y-1/2 drop-shadow-md will-change-transform lg:h-[5vw] lg:w-[5vw]"
  >
    <img
      v-for="(img, index) in images"
      :key="index"
      :src="img"
      alt="møgbille"
      :class="['absolute top-0 left-0 h-auto h-full w-full', { hidden: currentImage !== img }]"
    />
  </div>
</template>
